import { DOCUMENT } from "@angular/common";
import { inject, InjectionToken } from "@angular/core";

/**
 * Switches that hide finished features from users: build-time constants the product flips to
 * release a feature, and per-deployment settings Rss.Proxy writes into `index.html`.
 *
 * Either way the feature's code stays in the codebase untouched.
 */

/**
 * The eWIZ option to select derived OTA keys, hidden per RSS-1685 so users are not aware the
 * functionality exists yet.
 */
export const EWIZ_DERIVED_KEYS_VISIBLE: boolean = false;

/**
 * Injectable form of {@link EWIZ_DERIVED_KEYS_VISIBLE}, for the eWIZ derived-key controls.
 *
 * The eWIZ gate has behavior on both sides of it — the derived-key options are fetched only when
 * the option is offered — so a spec has to be able to exercise the flag as `true`. Overriding this
 * provider does that without the component's own field giving up `readonly`.
 */
export const EWIZ_DERIVED_KEYS_VISIBLE_TOKEN = new InjectionToken<boolean>("EWIZ_DERIVED_KEYS_VISIBLE", {
  providedIn: "root",
  factory: () => EWIZ_DERIVED_KEYS_VISIBLE
});

/**
 * Name of the `<meta>` tag in `index.html` whose content Rss.Proxy fills from the
 * `RSS_OTA_KEY_DERIVATION_VISIBLE` environment variable.
 */
export const OTA_KEY_DERIVATION_SCREEN_VISIBLE_META = "rs-ota-key-derivation-visible";

/**
 * Whether this deployment shows the Key Derivation menu entry under Key Vault and serves its
 * page: `true` only when the page's {@link OTA_KEY_DERIVATION_SCREEN_VISIBLE_META} tag reads `true`.
 *
 * @remarks Off everywhere unless the deployment sets `RSS_OTA_KEY_DERIVATION_VISIBLE=true` on the
 * webapp container (RSS-2241). The HsmAdapter refuses creating and deleting derivations with its own
 * `Features:OtaKeyDerivationManagement` setting, so both are turned on together.
 */
export const OTA_KEY_DERIVATION_SCREEN_VISIBLE = new InjectionToken<boolean>("OTA_KEY_DERIVATION_SCREEN_VISIBLE", {
  providedIn: "root",
  factory: () =>
    inject(DOCUMENT).querySelector(`meta[name="${OTA_KEY_DERIVATION_SCREEN_VISIBLE_META}"]`)?.getAttribute("content") === "true"
});
